import { useId } from "react";
import type { GameStatus } from "../types/domain";
import { GameStatusPill } from "./GameStatusPill";

interface HandPoints {
  leftScore: number;
  rightScore: number;
}

interface ScoreHeaderProps {
  modeLabel: string;
  leftLabel: string;
  rightLabel: string;
  leftScore: number;
  rightScore: number;
  targetScore?: number | null;
  subtitle?: string;
  variant?: "tournament" | "quick";
  status?: GameStatus | null;
  leftPlayers?: string;
  rightPlayers?: string;
  hands?: HandPoints[];
}

interface SparklineProps {
  hands: HandPoints[];
  variant: "tournament" | "quick";
}

const VIEW_W = 100;
const VIEW_H = 40;
const PADDING = 4;

function buildPath(points: [number, number][]): string {
  return points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
}

function ScoreSparkline({ hands, variant }: SparklineProps) {
  const uid = useId();
  const gradLeftId = `${uid}-grad-left`;
  const gradRightId = `${uid}-grad-right`;

  // Build cumulative totals starting from 0
  const cumLeft: number[] = [0];
  const cumRight: number[] = [0];
  for (const h of hands) {
    cumLeft.push(cumLeft[cumLeft.length - 1] + h.leftScore);
    cumRight.push(cumRight[cumRight.length - 1] + h.rightScore);
  }

  const allValues = [...cumLeft, ...cumRight];
  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);
  const range = dataMax - dataMin || 1;

  const toX = (i: number) => (i / (cumLeft.length - 1)) * VIEW_W;
  const toY = (v: number) =>
    VIEW_H - PADDING - ((v - dataMin) / range) * (VIEW_H - PADDING * 2);

  const leftPoints: [number, number][] = cumLeft.map((v, i) => [
    toX(i),
    toY(v),
  ]);
  const rightPoints: [number, number][] = cumRight.map((v, i) => [
    toX(i),
    toY(v),
  ]);

  const leftLine = buildPath(leftPoints);
  const rightLine = buildPath(rightPoints);

  // Area fill: close path along the bottom
  const leftArea =
    leftLine +
    ` L${leftPoints[leftPoints.length - 1][0]},${VIEW_H} L${leftPoints[0][0]},${VIEW_H} Z`;
  const rightArea =
    rightLine +
    ` L${rightPoints[rightPoints.length - 1][0]},${VIEW_H} L${rightPoints[0][0]},${VIEW_H} Z`;

  const isTournament = variant === "tournament";
  const leftColor = isTournament ? "#93c5fd" : "#3b82f6";
  const rightColor = isTournament ? "#fde68a" : "#f59e0b";
  const svgOpacity = isTournament ? 0.4 : 0.3;

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        opacity: svgOpacity,
      }}
    >
      <defs>
        <linearGradient id={gradLeftId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={leftColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={leftColor} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={gradRightId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={rightColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={rightColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Area fills */}
      <path d={leftArea} fill={`url(#${gradLeftId})`} stroke="none" />
      <path d={rightArea} fill={`url(#${gradRightId})`} stroke="none" />
      {/* Lines */}
      <path d={leftLine} fill="none" stroke={leftColor} strokeWidth="0.8" />
      <path d={rightLine} fill="none" stroke={rightColor} strokeWidth="0.8" />
    </svg>
  );
}

export function ScoreHeader({
  modeLabel,
  leftLabel,
  rightLabel,
  leftScore,
  rightScore,
  targetScore,
  subtitle,
  variant = "tournament",
  status,
  leftPlayers,
  rightPlayers,
  hands,
}: ScoreHeaderProps) {
  const leftWon = targetScore != null && leftScore >= targetScore;
  const rightWon = targetScore != null && rightScore >= targetScore;

  const isTournament = variant === "tournament";

  const panel = isTournament
    ? "score-panel border-t-2 border-t-ember"
    : "rounded-2xl border border-gray-200 bg-white px-5 py-4 text-ink";

  const muted = isTournament ? "text-white/60" : "text-smoke";
  const subtitleColor = isTournament ? "text-white/40" : "text-gray-400";

  const showSparkline = hands != null && hands.length >= 1;

  return (
    <section className={`${panel} relative overflow-hidden`}>
      {showSparkline && <ScoreSparkline hands={hands!} variant={variant} />}
      <div className="flex items-center justify-between gap-2">
        <p className={`text-xs font-medium uppercase tracking-wider ${muted}`}>
          {modeLabel}
        </p>
        {status && <GameStatusPill status={status} />}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-4">
        <div>
          <div className={`text-sm font-medium ${muted}`}>{leftLabel}</div>
          {leftPlayers && (
            <div className={`text-xs font-light ${muted}`}>{leftPlayers}</div>
          )}
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-4xl font-bold">{leftScore}</span>
            {leftWon && <span className="text-sm leading-none">🏆</span>}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-sm font-medium ${muted}`}>{rightLabel}</div>
          {rightPlayers && (
            <div className={`text-xs font-light ${muted}`}>{rightPlayers}</div>
          )}
          <div className="mt-1 flex items-center justify-end gap-1.5">
            <span className="text-4xl font-bold">{rightScore}</span>
            {rightWon && <span className="text-sm leading-none">🏆</span>}
          </div>
        </div>
      </div>
      {subtitle && (
        <p className={`mt-3 text-center text-xs ${subtitleColor}`}>
          {subtitle}
        </p>
      )}
    </section>
  );
}
